import React, { useEffect, useState } from 'react';
import { Hint, HintProps } from '../Hint';
import { safeLocalStorage } from '../../utils/safeStorage';

export interface OnboardingHintProps extends Omit<HintProps, 'open' | 'onClose'> {
  id: string;
  onShow?: () => void;
  onClose?: () => void;
  preventLocalStorage?: boolean;
  prerequisites?: Array<string>;
  externalDismiss?: (fn: () => void) => void;
  dontShow?: boolean;
}

export const OnboardingHint: React.FC<OnboardingHintProps> = ({ id, ...props }) => {
  const localStorageKey = `hint-${id}`;
  const [shownToUser, setShownToUser] = useState(true);

  useEffect(() => {
    // we only want to run this on mount
    // if all previous events happened, then the hint is shown
    const prerequisitesMet = props.prerequisites?.length
      ? props.prerequisites.every(prerequisite => safeLocalStorage.getItem(`hint-${prerequisite}`) === 'true')
      : true;
    
    if (prerequisitesMet) { 
      const idIsTrue = safeLocalStorage.getItem(localStorageKey) === 'true';
      setShownToUser(idIsTrue);
      if (!idIsTrue) {
        props.onShow?.();
      }
    }
   
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dismiss = () => {
    if (!props.preventLocalStorage) {
      safeLocalStorage.setItem(localStorageKey, 'true');
    }
    setShownToUser(true);
    props?.onClose?.();
  };

  props.externalDismiss?.(dismiss);
  const actions = props.actions; 

  const createOnClickHandler = (originalOnClick: () => void, options?: { withoutDismiss?: boolean } ) => {
    if (options?.withoutDismiss) {
      return originalOnClick;
    } else {
      return () => {
        originalOnClick();
        dismiss();
      };
    }
  };

  if (actions) {
    if (actions.primary) {
      actions.primary.onClick = createOnClickHandler(actions.primary.onClick);
    }
    if (actions.secondary) {
      actions.secondary.onClick = createOnClickHandler(
        actions.secondary.onClick, { withoutDismiss: actions.secondary.withoutDismiss }
      );
    }
  }

  return <Hint
    {...props}
    actions={actions}
    open={props.dontShow ? false : !shownToUser}
    onClose={dismiss}
  />;
};

export default OnboardingHint;
